import { readableMediaUrl } from '../../services/mediaRead';
import { tileUrl } from '../content/tileUrl';

/*
 * Upscale's sizes (Tarek, 2 Nov: "why can't I pick 1K, 2K or 4K?"). Crystal takes a factor, not a
 * size, so each size is worked out from the picture: its long edge at the target's, as Flow's
 * utils/upscaleTargets.js does. Nothing says Crystal keeps alpha, so a transparent picture stays on
 * Bria, which is told to keep it; ai-back's row fixes Bria's `desired_increase` at 2.
 */

export type UpscaleTarget = '1K' | '2K' | '4K';

/** Long edges, Flow's for 2K and 4K. */
const LONG_EDGE: Record<UpscaleTarget, number> = { '1K': 1024, '2K': 2048, '4K': 3840 };
export const UPSCALE_TARGETS = Object.keys(LONG_EDGE) as UpscaleTarget[];
/** What a picture opens on: the cheapest size worth having, then the larger. */
const START: readonly UpscaleTarget[] = ['2K', '4K'];
/** ai-back keeps Crystal's factor within 12; only a tiny picture asks for more. */
const MOST = 12;
/** Within 2% of the size is already there: a 1.02× run is a charge for the same picture. */
const NEAR = 0.98;
/** Bria stops at 10 MP, however far the factor would go. */
const BRIA_PIXELS = 10_000_000;

/** The factor that brings a picture to a size, rounded up so it reaches it; null when it is there already. */
export function upscaleFactor(target: UpscaleTarget, width: number, height: number): number | null {
  const edge = LONG_EDGE[target], long = Math.max(width, height);
  if (!(long > 0) || long >= edge * NEAR) return null;
  return Math.min(MOST, Math.ceil(edge / long * 100 - 1e-9) / 100);
}

/** The size a picture comes back at, this much bigger. */
export const upscaled = (width: number, height: number, factor: number) => ({ width: Math.round(width * factor), height: Math.round(height * factor) });

/** The size Bria answers a transparent picture at: double, within its ceiling. */
export const doubled = (width: number, height: number) => upscaled(width, height, Math.min(2, Math.sqrt(BRIA_PIXELS / (width * height))));

/** The size a picture opens on, or the size still open to it, or null when it is past 4K. */
export function startingTarget(width: number, height: number): UpscaleTarget | null {
  return START.find(target => upscaleFactor(target, width, height) !== null) ?? null;
}

/**
 * Whether any of a picture shows through, read from a small copy. One that cannot be read counts
 * as transparent: doubling it is a smaller loss than painting a cut-out's background in.
 */
export async function hasTransparency(src: string, mediaAssetId?: string): Promise<boolean> {
  try {
    const image = new Image(); image.crossOrigin = 'anonymous'; image.src = tileUrl(await readableMediaUrl(src, mediaAssetId), 128);
    await image.decode();
    const k = Math.min(1, 128 / Math.max(image.naturalWidth, image.naturalHeight));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(image.naturalWidth * k)); canvas.height = Math.max(1, Math.round(image.naturalHeight * k));
    const g = canvas.getContext('2d', { willReadFrequently: true })!;
    g.drawImage(image, 0, 0, canvas.width, canvas.height);
    const { data } = g.getImageData(0, 0, canvas.width, canvas.height);
    for (let at = 3; at < data.length; at += 4) if (data[at]! < 250) return true;
    return false;
  } catch {
    return true;
  }
}
